import { MiniGameStatus } from '@/__generated__/graphql'
import { useTranslation } from 'next-i18next/pages'
import { GET_USER_STATS_DATA } from '@/api/query'
import { AvatarCircle } from '@/components/atoms/Avatar'
import { GradientText } from '@/components/atoms/GradientText/GradientText'
import { StepBadge, StepBadgeColor } from '@/components/atoms/StepBadge/StepBadge'
import { useAppModal, AppModals } from '@/context/ModalProvider'
import { Skeleton, Flex, Box, Text } from '@chakra-ui/react'
import { useQuery } from '@apollo/client'
import { Coin } from '@ninjablaze/sdk-ts'
import { TokenMoney } from '@ninjablaze/sdk-ts'

import { FiatOrCryptoAmount, Amount } from '@/components/atoms/Amount/Amount'

export const UserGameResult = ({
  address,
  payout,
  result,
  multiplier,
  disableFiatConversion = false,
}: {
  address: string
  payout: Coin
  result: MiniGameStatus
  multiplier: string
  disableFiatConversion?: boolean
}) => {
  const { t } = useTranslation()
  const modal = useAppModal()
  const { data } = useQuery(GET_USER_STATS_DATA, {
    variables: { address },
  })

  const numberOfNfts = data?.nfts?.totalCount
  const isNftOwner = Boolean(numberOfNfts)

  if (!data) {
    return (
      <Skeleton
        startColor={'ninja.500'}
        endColor={'ninja.400'}
        h={{ base: '175px', md: '87px' }}
        mb={3}
        rounded={'xl'}
      />
    )
  }

  const token = new TokenMoney(payout.amount, payout.denom)

  return (
    <Flex gap={{ base: 4, md: 8 }} mb={3} flexDir={{ base: 'column', md: 'row' }}>
      <Flex
        cursor={'pointer'}
        onClick={() => modal.openModalNew(AppModals.UserStats, { address })}
        alignItems={'center'}
        justifyContent={{ base: 'center', md: 'start' }}
        gap={3}
        rounded={'xl'}
      >
        <AvatarCircle size={'lg'} offset={4} src={data.user?.profileAvatar || ''} />
        <Flex flexDir={'column'} alignItems={'start'}>
          {isNftOwner ? (
            <GradientText
              numberOfNfts={numberOfNfts!}
              props={{
                fontWeight: 700,
                fontSize: 'xl',
              }}
            >
              {data?.user?.profileUserName}
            </GradientText>
          ) : (
            <Text fontWeight={700} fontSize={'xl'}>
              {data?.user?.profileUserName}
            </Text>
          )}
          <StepBadge
            color={result === MiniGameStatus.Win ? StepBadgeColor.GREEN : StepBadgeColor.RED}
            content={'x' + multiplier}
          />
        </Flex>
      </Flex>
      <Box flexBasis={'full'} rounded={'3xl'} border={'2px solid'} py={4} px={6} borderColor={'ninja.500'}>
        <Text fontSize={'sm'} color={'ninja.200'} fontWeight={700}>
          {t('common.prize')}
        </Text>
        {disableFiatConversion ? (
          <Amount value={token} wrapperStyles={{ fontSize: 'xl' }} iconSize={'22px'} />
        ) : (
          <FiatOrCryptoAmount value={token} wrapperStyles={{ fontSize: 'xl' }} iconSize={'22px'} />
        )}
      </Box>
    </Flex>
  )
}
